import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {ColumnDef} from "@tanstack/react-table";
import {formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    serverTableQueryKey,
    useServerTable,
} from "../../../common/serverTable";
import {InsightTargetV4Service} from "../../services/insight-target-v4.service";
import {BUDGET_MANAGER_TITLE_ID, BUDGET_SCREEN_HEIGHT, BudgetManagerHeader} from "./BudgetManagerHeader";
import {CRM_BUDGETS_TABLE_ID, targetRows, type InsightTargetPeriod} from "./insightTargets";
import InsightsTargetEditor from "./InsightsTargetEditor";
import {useSelectInsightTarget} from "./useInsightTargets";

/** Budgets are a short, unpaged list, so the table shows every row on one page. */
const BUDGET_PAGE_SIZE = 999;

interface InsightsTargetsDialogProps {
    open: boolean;
    onClose: () => void;
}

type TargetEditorState = {mode: "new"} | {mode: "edit"; period: InsightTargetPeriod};

const parseIsoDate = (value: string) => new Date(`${value}T00:00:00`);

const isWholeMonth = (start: Date, end: Date) => (
    start.getDate() === 1 &&
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    end.getDate() === new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate()
);

const isWholeYear = (start: Date, end: Date) => (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === 0 &&
    start.getDate() === 1 &&
    end.getMonth() === 11 &&
    end.getDate() === 31
);

const formatPeriod = (period: InsightTargetPeriod) => (
    `${formatFullDMY(parseIsoDate(period.from))} – ${formatFullDMY(parseIsoDate(period.to))}`
);

export function InsightsTargetsDialog({open, onClose}: InsightsTargetsDialogProps) {
    const {t, i18n} = useTranslation();
    const queryClient = useQueryClient();
    const [editor, setEditor] = useState<TargetEditorState | null>(null);
    const wasOpen = useRef(false);
    const selectTargetMutation = useSelectInsightTarget();
    const resetSelectTarget = selectTargetMutation.reset;

    const locale = i18n.resolvedLanguage || i18n.language;
    const monthYearFormatter = useMemo(
        () => new Intl.DateTimeFormat(locale, {month: "long", year: "numeric"}),
        [locale],
    );
    const displayName = useCallback((period: InsightTargetPeriod) => {
        const customName = period.name?.trim();
        if (customName) return customName;

        // Periods saved before names were introduced remain understandable until somebody edits them.
        const start = parseIsoDate(period.from);
        const end = parseIsoDate(period.to);

        if (isWholeMonth(start, end)) return monthYearFormatter.format(start);
        if (isWholeYear(start, end)) return String(start.getFullYear());
        return t("Custom range");
    }, [monthYearFormatter, t]);

    const refreshBudgets = useCallback(
        () => queryClient.invalidateQueries(serverTableQueryKey(CRM_BUDGETS_TABLE_ID)),
        [queryClient],
    );
    const toggleActive = useCallback((period: InsightTargetPeriod) => {
        selectTargetMutation.mutate(
            period.active ? {} : {from: period.from, to: period.to},
            {onSuccess: () => refreshBudgets()},
        );
    }, [refreshBudgets, selectTargetMutation]);

    const selecting = selectTargetMutation.isLoading;
    const columns = useMemo<ColumnDef<InsightTargetPeriod>[]>(() => [
        {
            id: "active",
            header: t("Active"),
            cell: ({row}) => (
                <Box data-no-row-click onClick={event => event.stopPropagation()} sx={{display: "flex"}}>
                    <Checkbox
                        checked={row.original.active}
                        onChange={() => toggleActive(row.original)}
                        disabled={selecting}
                        inputProps={{"aria-label": `${t("Active")}: ${displayName(row.original)}`}}
                        sx={{p: 0.5}}
                    />
                </Box>
            ),
            size: 72,
        },
        {
            id: "name",
            header: t("Budget"),
            accessorFn: period => period.name,
            cell: ({row}) => (
                <Typography variant={"body1"} noWrap>
                    {displayName(row.original)}
                </Typography>
            ),
            size: 320,
            meta: {grow: true},
        },
        {
            id: "period",
            header: t("Time range"),
            accessorFn: period => period.from,
            cell: ({row}) => (
                <Typography variant={"body1"} color={"text.secondary"} noWrap>
                    {formatPeriod(row.original)}
                </Typography>
            ),
            size: 240,
        },
        {
            id: "actions",
            header: "",
            cell: ({row}) => (
                <Stack direction={"row"} justifyContent={"flex-end"} data-no-row-click>
                    <Tooltip arrow title={t("Edit budget")}>
                        <IconButton
                            size={"small"}
                            aria-label={`${t("Edit budget")}: ${displayName(row.original)}`}
                            onClick={() => setEditor({mode: "edit", period: row.original})}
                        >
                            <EditOutlinedIcon fontSize={"small"}/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
            size: 72,
            meta: {align: "right"},
        },
    ], [displayName, selecting, t, toggleActive]);

    const controller = useServerTable<InsightTargetPeriod>({
        tableId: CRM_BUDGETS_TABLE_ID,
        presentation: "embedded",
        defaultPageSize: BUDGET_PAGE_SIZE,
        columns,
        getRowId: period => period.id,
        onRowClick: period => setEditor({mode: "edit", period}),
        enabled: open,
        // The endpoint returns every period at once, already ordered by `targetRows`.
        queryFn: async () => {
            const periods = targetRows(await InsightTargetV4Service.list());

            return {content: periods, totalElements: periods.length};
        },
    });
    const periods = useMemo(() => controller.query.data?.content ?? [], [controller.query.data]);

    useEffect(() => {
        if (open && !wasOpen.current) {
            setEditor(null);
            resetSelectTarget();
        }
        wasOpen.current = open;
    }, [open, resetSelectTarget]);

    const closeDialog = () => {
        setEditor(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={closeDialog}
            fullWidth
            maxWidth={"md"}
            aria-labelledby={BUDGET_MANAGER_TITLE_ID}
        >
            {editor != null ? (
                <InsightsTargetEditor
                    open={open}
                    period={editor.mode === "edit" ? editor.period : undefined}
                    existingPeriods={periods}
                    onBack={() => setEditor(null)}
                    onClose={closeDialog}
                    onSaved={() => {
                        refreshBudgets();
                        setEditor(null);
                    }}
                />
            ) : (
                <>
                    <BudgetManagerHeader title={t("Manage budgets")} onClose={closeDialog}/>

                    <DialogContent
                        dividers
                        sx={{p: 2, height: BUDGET_SCREEN_HEIGHT, display: "flex", flexDirection: "column", gap: 1}}
                    >
                        {periods.length > 0 && (
                            <Typography variant={"body2"} color={"text.secondary"} sx={{px: 1}}>
                                {t("Select one budget to show it in insights. Select it again to hide all budgets.")}
                            </Typography>
                        )}

                        {(controller.query.isError || selectTargetMutation.isError) && (
                            <Alert severity={"error"} sx={{mx: 1}}>
                                {t(controller.query.isError
                                    ? "Could not load the budgets."
                                    : "Could not update the active budget.")}
                            </Alert>
                        )}

                        <Box sx={{flex: 1, minHeight: 0}}>
                            <ServerTableProvider controller={controller}>
                                <ServerTableFrame>
                                    <ServerTable showPagination={false}/>
                                </ServerTableFrame>
                            </ServerTableProvider>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{px: 3, py: 2}}>
                        <Button color={"inherit"} onClick={closeDialog} sx={{ml: "auto"}}>
                            {t("Close")}
                        </Button>

                        <Button variant={"contained"} startIcon={<AddIcon/>} onClick={() => setEditor({mode: "new"})}>
                            {t("Add budget")}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}

export default InsightsTargetsDialog;
